import React, { useState } from 'react';
import {
  Database,
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  Type,
  Hash,
  Calendar,
  ToggleLeft,
  List,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Switch } from '../../ui/switch';

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'boolean', label: 'Yes/No', icon: ToggleLeft },
  { value: 'select', label: 'Dropdown', icon: List },
];

/**
 * Custom Fields Section
 * 
 * Configure agency-specific custom fields for cases, clients, etc.
 */
const CustomFieldsSection = ({ agency, agencyId, isReadOnly }) => {
  const [fields, setFields] = useState(agency?.custom_fields || [
    { id: 1, name: 'Referral Source', key: 'referral_source', type: 'select', enabled: true, context: 'case' },
    { id: 2, name: 'Priority Level', key: 'priority_level', type: 'select', enabled: true, context: 'case' },
    { id: 3, name: 'Initial Consultation Date', key: 'consultation_date', type: 'date', enabled: true, context: 'client' },
  ]);
  
  const [editingId, setEditingId] = useState(null);
  
  return (
    <div className="space-y-6" data-testid="custom-fields-section">
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
              <Database className="text-violet-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Custom Fields</h2>
              <p className="text-sm text-slate-500">
                Define custom data fields for your agency workflows
              </p>
            </div>
          </div>
          
          {!isReadOnly && (
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus size={16} className="mr-2" />
              Add Field
            </Button>
          )}
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Total Fields</p>
          <p className="text-2xl font-bold text-slate-900">{fields.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Active</p>
          <p className="text-2xl font-bold text-emerald-600">{fields.filter(f => f.enabled).length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Contexts</p>
          <p className="text-2xl font-bold text-blue-600">{new Set(fields.map(f => f.context)).size}</p>
        </div>
      </div>
      
      {/* Fields List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Field Name</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Key</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Type</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Context</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => {
              const TypeIcon = FIELD_TYPES.find(t => t.value === field.type)?.icon || Type;
              
              return (
                <tr key={field.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{field.name}</td>
                  <td className="px-4 py-3">
                    <code className="text-xs bg-slate-100 px-2 py-1 rounded">{field.key}</code>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <TypeIcon size={14} className="text-slate-400" />
                      <span className="text-sm text-slate-600">{FIELD_TYPES.find(t => t.value === field.type)?.label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="capitalize">{field.context}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Switch checked={field.enabled} disabled={isReadOnly} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!isReadOnly && (
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm"><Edit2 size={14} /></Button>
                        <Button variant="ghost" size="sm" className="text-red-500"><Trash2 size={14} /></Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomFieldsSection;
